import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  MainTableFilterSearchByModel,
  MainTableSortingType,
} from 'src/app/shared/models/main-table-filter.model';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { DEPARTMENT_SCOPED_FILTER_PROPERTIES } from '../config/department-scoped-filter-properties.config';

export interface UserTableFilterSettings {
  modulePageSizes: { [key: string]: number };
  moduleSearchBy: { [key: string]: MainTableFilterSearchByModel[] };
  moduleSorting: { [key: string]: { orderBy: string; ascDesc: MainTableSortingType } };
}

const TABLE_FILTERS_STORAGE_KEY_PREFIX = 'tableFilters';
const FILTER_SAVING_ENABLED_KEY_PREFIX = 'filterSavingEnabled';
const LEGACY_FILTER_PROPERTY_MAP: Record<string, string> = {
  access: 'accessPositions',
  responsible: 'responsiblePositions',
  responsibleForFollowUp: 'responsibleForFollowUpPositions',
  responsibleForClosing: 'responsibleForClosingPositions',
};

@Injectable({
  providedIn: 'root',
})
export class TableFilterStorageService {
  constructor(private store: Store) {}

  /**
   * Checks if filter saving is enabled for the current user.
   * Returns true by default if no preference is stored, and sets it explicitly.
   */
  isFilterSavingEnabled(): boolean {
    const key = this.getFilterSavingEnabledKey();
    if (!key) {
      return true;
    }
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        // Set default value explicitly for new users
        localStorage.setItem(key, 'true');
        return true;
      }
      return stored === 'true';
    } catch {
      return true;
    }
  }

  /**
   * Checks if there are any saved filters for the current user.
   */
  hasSavedFilters(): boolean {
    const settings = this.loadSettings();
    if (!settings) {
      return false;
    }
    // Check if any of the filter objects have data
    const hasPageSizes = Object.keys(settings.modulePageSizes).length > 0;
    const hasSearchBy = Object.keys(settings.moduleSearchBy).length > 0;
    const hasSorting = Object.keys(settings.moduleSorting).length > 0;

    return hasPageSizes || hasSearchBy || hasSorting;
  }

  /**
   * Enables or disables filter saving for the current user.
   * When disabled, clears all existing saved filters for this user.
   */
  setFilterSavingEnabled(enabled: boolean): void {
    const key = this.getFilterSavingEnabledKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, String(enabled));
      if (!enabled) {
        this.clearSettings();
      }
    } catch (error) {
      console.error('Failed to save filter saving preference:', error);
    }
  }

  private getFilterSavingEnabledKey(): string | null {
    const userId = this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id ?? null;
    if (!userId) {
      return null;
    }
    return `${FILTER_SAVING_ENABLED_KEY_PREFIX}_${userId}`;
  }

  private getStorageKey(): string | null {
    const userId = this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id ?? null;
    if (!userId) {
      console.warn(
        '[TableFilterStorageService] Could not determine user ID when creating storage key for table filters.'
      );
      return null;
    }
    return `${TABLE_FILTERS_STORAGE_KEY_PREFIX}_${userId}`;
  }

  saveSettings(settings: UserTableFilterSettings): void {
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save table filter settings to localStorage:', error);
    }
  }

  loadSettings(): UserTableFilterSettings | null {
    const key = this.getStorageKey();
    if (!key) {
      return null;
    }
    try {
      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }
      const settings = JSON.parse(stored) as UserTableFilterSettings;
      const migratedSettings = this.migrateLegacyFilterProperties(settings);

      if (migratedSettings.changed) {
        this.saveSettings(migratedSettings.settings);
      }

      return migratedSettings.settings;
    } catch (error) {
      console.error('Failed to load table filter settings from localStorage:', error);
      return null;
    }
  }

  clearSettings(): void {
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear table filter settings from localStorage:', error);
    }
  }

  saveModulePageSize(module: string, pageSize: number): void {
    if (!this.isFilterSavingEnabled()) return;
    const currentSettings = this.loadSettings() || this.getDefaultSettings();
    currentSettings.modulePageSizes[module] = pageSize;
    this.saveSettings(currentSettings);
  }

  saveModuleSearchBy(module: string, searchBy: MainTableFilterSearchByModel[]): void {
    if (!this.isFilterSavingEnabled()) return;
    const currentSettings = this.loadSettings() || this.getDefaultSettings();
    currentSettings.moduleSearchBy[module] = searchBy;
    this.saveSettings(currentSettings);
  }

  saveModuleSorting(
    module: string,
    sorting: { orderBy: string; ascDesc: MainTableSortingType }
  ): void {
    if (!this.isFilterSavingEnabled()) return;
    const currentSettings = this.loadSettings() || this.getDefaultSettings();
    currentSettings.moduleSorting[module] = sorting;
    this.saveSettings(currentSettings);
  }

  /**
   * Clears all filter settings for a specific module from localStorage.
   * Called when user explicitly clicks "Clear all filters" button.
   */
  clearModuleFilters(module: string): void {
    const currentSettings = this.loadSettings();
    if (!currentSettings) {
      return;
    }
    delete currentSettings.moduleSearchBy[module];
    delete currentSettings.moduleSorting[module];
    this.saveSettings(currentSettings);
  }

  /**
   * Clears all filter settings on tenant change.
   * Called when user switches to a different tenant.
   */
  clearAllFiltersOnTenantChange(): void {
    const currentSettings = this.loadSettings();
    if (!currentSettings) {
      return;
    }
    currentSettings.moduleSearchBy = {};
    currentSettings.moduleSorting = {};
    this.saveSettings(currentSettings);
  }

  clearDepartmentScopedFiltersOnDepartmentChange(): void {
    const currentSettings = this.loadSettings();
    if (!currentSettings) {
      return;
    }

    // For each module's searchBy filters, remove only the department-scoped properties
    Object.keys(currentSettings.moduleSearchBy).forEach((module) => {
      const filters = currentSettings.moduleSearchBy[module];
      if (filters && filters.length > 0) {
        currentSettings.moduleSearchBy[module] = filters.filter(
          (filter) => !DEPARTMENT_SCOPED_FILTER_PROPERTIES.includes(filter.property)
        );
      }
    });

    this.saveSettings(currentSettings);
  }

  private getDefaultSettings(): UserTableFilterSettings {
    return {
      modulePageSizes: {},
      moduleSearchBy: {},
      moduleSorting: {},
    };
  }

  private migrateLegacyFilterProperties(settings: UserTableFilterSettings): {
    changed: boolean;
    settings: UserTableFilterSettings;
  } {
    let changed = false;
    const moduleSearchBy = Object.fromEntries(
      Object.entries(settings.moduleSearchBy || {}).map(([module, filters]) => [
        module,
        (filters || []).map((filter) => {
          const migratedProperty = LEGACY_FILTER_PROPERTY_MAP[filter.property];
          if (!migratedProperty) {
            return filter;
          }

          changed = true;
          return new MainTableFilterSearchByModel(migratedProperty, filter.value, filter.operand);
        }),
      ])
    );

    return {
      changed,
      settings: {
        ...settings,
        moduleSearchBy,
      },
    };
  }
}
